'use client';

import { useId, useMemo, useState } from 'react';

import {
  CalendarClock,
  ChevronDown,
  MapPin,
  UserRound,
  Users,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { TemplateInfo } from '@kit/bookings';
import { formatLocaleDate, formatTimeToAmPm } from '@kit/shared/utils';
import { Button } from '@kit/ui/button';
import { CurrencyIcon } from '@kit/ui/currency-icon';
import { cn } from '@kit/ui/utils';

import { useServiceCategoryNavigation } from '../_lib/hooks/use-service-category-navigation';
import { SELECTED_SERVICE_TRANSITION_NAME } from '../_lib/selected-service-transition';

interface ServiceListProps {
  /** Public templates for the active sport + booking type. */
  services: TemplateInfo[];
  selectedTemplateId: string;
  isMember: boolean;
  /** Canonical duration label builder (shared with the duration-chip flow). */
  formatDuration: (minutes: number) => string;
  onSelect: (templateId: string, durationLabel: string) => void;
}

/** Stable bucket key for templates with no category. */
const UNCATEGORIZED = '__uncategorized__';

/** Category navigation only earns its space with multiple sections. */
const MIN_CATEGORIES_FOR_CHIPS = 2;

/** Description length past which the expanded card truncates behind "Read more". */
const DESCRIPTION_CLAMP = 160;

/**
 * One row of the list: either a standalone template or a service with several
 * options (templates sharing a `variant_group`). Every option is still a real
 * template, so selection always resolves to one template id.
 */
type ServiceEntry =
  | { kind: 'single'; template: TemplateInfo }
  | {
      kind: 'group';
      id: string;
      name: string;
      name_alt: string | null;
      description: string | null;
      description_alt: string | null;
      options: TemplateInfo[];
    };

function customerPrice(template: TemplateInfo, isMember: boolean) {
  return isMember && template.member_price != null
    ? template.member_price
    : template.normal_price;
}

/**
 * Collapse templates into list entries. Templates that share a group become one
 * entry with the group head's name/description; a group with a single visible
 * member (its siblings filtered out for this location / membership) renders as
 * a plain row so the customer never sees a one-option picker.
 */
function buildEntries(services: TemplateInfo[]): ServiceEntry[] {
  const groups = new Map<string, TemplateInfo[]>();
  const order: Array<{ groupId: string } | { template: TemplateInfo }> = [];

  for (const service of services) {
    const groupId = service.variant_group?.id;
    if (!groupId) {
      order.push({ template: service });
      continue;
    }
    const bucket = groups.get(groupId);
    if (bucket) {
      bucket.push(service);
    } else {
      groups.set(groupId, [service]);
      order.push({ groupId });
    }
  }

  return order.map((item): ServiceEntry => {
    if ('template' in item) return { kind: 'single', template: item.template };

    const options = groups.get(item.groupId)!;
    const head = options[0]!.variant_group!;

    if (options.length === 1) {
      return { kind: 'single', template: options[0]! };
    }

    options.sort((a, b) => a.duration - b.duration);

    return {
      kind: 'group',
      id: head.id,
      name: head.name,
      name_alt: head.name_alt,
      description: head.description,
      description_alt: head.description_alt,
      options,
    };
  });
}

function entryCategory(entry: ServiceEntry): string {
  const raw =
    entry.kind === 'single'
      ? entry.template.category
      : (entry.options[0]!.variant_group?.category ??
        entry.options[0]!.category);
  return raw?.trim() || UNCATEGORIZED;
}

function entryName(entry: ServiceEntry) {
  return entry.kind === 'single' ? entry.template.name : entry.name;
}

function groupByCategory(entries: ServiceEntry[]) {
  const groups = new Map<string, ServiceEntry[]>();

  for (const entry of entries) {
    const key = entryCategory(entry);
    const bucket = groups.get(key);
    if (bucket) {
      bucket.push(entry);
    } else {
      groups.set(key, [entry]);
    }
  }

  // Sort each bucket by name; sort buckets alphabetically, uncategorized last.
  for (const bucket of groups.values()) {
    bucket.sort((a, b) => entryName(a).localeCompare(entryName(b)));
  }

  return Array.from(groups.entries()).sort(([a], [b]) => {
    if (a === UNCATEGORIZED) return 1;
    if (b === UNCATEGORIZED) return -1;
    return a.localeCompare(b);
  });
}

export function ServiceList({
  services,
  selectedTemplateId,
  isMember,
  formatDuration,
  onSelect,
}: ServiceListProps) {
  const { t, i18n } = useTranslation('bookings');
  const isArabic = i18n.language === 'ar';

  // Tenants author `name` in their primary language and `name_alt` (typically
  // Arabic) as an optional overlay — fall back to the primary when unset.
  const localized = (primary: string, alt: string | null | undefined) =>
    isArabic && alt?.trim() ? alt : primary;

  const entries = useMemo(() => buildEntries(services), [services]);
  const grouped = useMemo(() => groupByCategory(entries), [entries]);

  const showChips = grouped.length >= MIN_CATEGORIES_FOR_CHIPS;
  const listId = useId();
  const sectionIds = useMemo(
    () => grouped.map((_, index) => `${listId}-category-${index}`),
    [grouped, listId],
  );
  const { listRef, navigationRef, activeSectionId, scrollToSection } =
    useServiceCategoryNavigation(sectionIds);
  const categoryLabel = (category: string) =>
    category === UNCATEGORIZED ? t('serviceOptions.otherServices') : category;

  // A group stays open while one of its options is selected; the customer can
  // also peek into another group without losing the selection.
  const [openGroupId, setOpenGroupId] = useState<string | null>(null);
  const selectedGroupId = useMemo(() => {
    const selectedEntry = entries.find(
      (entry) =>
        entry.kind === 'group' &&
        entry.options.some((option) => option.id === selectedTemplateId),
    );
    return selectedEntry?.kind === 'group' ? selectedEntry.id : null;
  }, [entries, selectedTemplateId]);

  if (services.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">
        {t('noServicesAvailable')}
      </p>
    );
  }

  return (
    <div ref={listRef} className="min-w-0 space-y-3" data-test="service-list">
      {showChips && (
        <nav
          ref={navigationRef}
          className="bg-background sticky top-[var(--service-category-top,0px)] z-20 flex gap-2 overflow-x-auto border-b px-1 py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          aria-label={t('chooseService')}
          data-test="service-category-chips"
        >
          {grouped.map(([category], index) => (
            <CategoryChip
              key={category}
              label={categoryLabel(category)}
              sectionId={sectionIds[index]!}
              isActive={activeSectionId === sectionIds[index]}
              onClick={() => scrollToSection(sectionIds[index]!)}
            />
          ))}
        </nav>
      )}

      <div className="space-y-4 px-1 py-0.5">
        {grouped.map(([category, items], index) => (
          <section
            key={category}
            id={sectionIds[index]}
            tabIndex={-1}
            aria-label={categoryLabel(category)}
            data-service-category-section
            data-test="service-category-section"
            className="space-y-2"
          >
            {(category !== UNCATEGORIZED || showChips) && (
              <h4 className="text-muted-foreground text-sm font-semibold">
                {categoryLabel(category)}
              </h4>
            )}

            <div className="space-y-2">
              {items.map((entry) =>
                entry.kind === 'single' ? (
                  <SingleServiceRow
                    key={entry.template.id}
                    template={entry.template}
                    name={localized(
                      entry.template.name,
                      entry.template.name_alt,
                    )}
                    description={localized(
                      entry.template.description ?? '',
                      entry.template.description_alt,
                    )}
                    price={customerPrice(entry.template, isMember)}
                    isSelected={selectedTemplateId === entry.template.id}
                    durationLabel={formatDuration(entry.template.duration)}
                    onSelect={() =>
                      onSelect(
                        entry.template.id,
                        formatDuration(entry.template.duration),
                      )
                    }
                  />
                ) : (
                  <GroupedServiceCard
                    key={entry.id}
                    name={localized(entry.name, entry.name_alt)}
                    description={localized(
                      entry.description ?? '',
                      entry.description_alt,
                    )}
                    options={entry.options}
                    isMember={isMember}
                    isOpen={
                      openGroupId === entry.id || selectedGroupId === entry.id
                    }
                    selectedTemplateId={selectedTemplateId}
                    formatDuration={formatDuration}
                    localized={localized}
                    onToggle={() =>
                      setOpenGroupId((current) =>
                        current === entry.id ? null : entry.id,
                      )
                    }
                    onSelectOption={(option) => {
                      setOpenGroupId(entry.id);
                      onSelect(option.id, formatDuration(option.duration));
                    }}
                  />
                ),
              )}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

/** What the customer has picked so far, beyond the service itself. */
export interface ServiceSelectionDetails {
  date?: Date | null;
  /** Slot start, "HH:mm". */
  time?: string;
  /** The chosen instructor (a person). */
  staffName?: string;
  /** The chosen room / court / space (a place). */
  spaceName?: string;
  /** Set only when the participants stepper is on the page. */
  participants?: number;
}

/**
 * The picked service, collapsed into the sticky strip so the date strip and
 * the next step stay in view. It grows with each choice (time, instructor) so
 * the customer always sees what they are paying for; "Change" reopens the list.
 */
export function SelectedServiceSummary({
  template,
  title,
  details,
  onChange,
}: {
  template: TemplateInfo;
  /**
   * Customer-facing name when the template's own name is internal: the
   * duration-chip flow never shows template names ("Outdoor 60"), so it
   * passes the activity ("Padel") instead.
   */
  title?: string;
  details: ServiceSelectionDetails;
  onChange: () => void;
}) {
  const { t, i18n } = useTranslation('bookings');
  const isArabic = i18n.language === 'ar';
  const localized = (primary: string, alt: string | null | undefined) =>
    isArabic && alt?.trim() ? alt : primary;

  const group = template.variant_group;
  const name =
    title ??
    (group
      ? localized(group.name, group.name_alt)
      : localized(template.name, template.name_alt));
  const option =
    group && !title
      ? localized(template.variant_label ?? '', template.variant_label_alt)
      : '';
  const when =
    details.date && details.time
      ? `${formatLocaleDate(details.date, i18n.language, {
          weekday: 'short',
          day: 'numeric',
          month: 'short',
          year: undefined,
        })} · ${formatTimeToAmPm(details.time, i18n.language)}`
      : '';

  return (
    <div
      className="border-primary bg-background animate-in fade-in rounded-lg border p-3 duration-200"
      style={{ viewTransitionName: SELECTED_SERVICE_TRANSITION_NAME }}
      data-test="selected-service-summary"
      data-template-id={template.id}
    >
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className="line-clamp-2 font-medium break-words">{name}</p>
          <p className="text-muted-foreground line-clamp-2 text-xs break-words">
            {/* The card has room to spell the length out ("60 minutes"). */}
          {[
            option,
            t('returnPage.durationMinutes', { count: template.duration }),
          ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          aria-label={t('serviceOptions.changeLabel')}
          data-test="selected-service-change"
          onClick={onChange}
        >
          {t('serviceOptions.change')}
        </Button>
      </div>

      {(when ||
        details.staffName ||
        details.spaceName ||
        details.participants) && (
        <div
          className="text-muted-foreground animate-in fade-in mt-2 flex flex-wrap gap-x-4 gap-y-1 border-t pt-2 text-xs duration-200"
          data-test="selected-service-details"
        >
          {when && (
            <span className="inline-flex items-center gap-1.5">
              <CalendarClock aria-hidden className="h-3.5 w-3.5" />
              {when}
            </span>
          )}
          {details.staffName && (
            <span className="inline-flex min-w-0 items-center gap-1.5">
              <UserRound aria-hidden className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{details.staffName}</span>
            </span>
          )}
          {details.participants && (
            <span className="inline-flex items-center gap-1.5">
              <Users aria-hidden className="h-3.5 w-3.5 shrink-0" />
              {t('splitInit.peopleCount', { count: details.participants })}
            </span>
          )}
          {details.spaceName && (
            <span className="inline-flex min-w-0 items-center gap-1.5">
              <MapPin aria-hidden className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{details.spaceName}</span>
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function CategoryChip({
  label,
  sectionId,
  isActive,
  onClick,
}: {
  label: string;
  sectionId: string;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <a
      href={`#${sectionId}`}
      aria-current={isActive ? 'location' : undefined}
      onClick={(event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
          return;
        event.preventDefault();
        onClick();
      }}
      className={cn(
        'focus-visible:ring-ring inline-flex min-h-11 shrink-0 items-center rounded-full border px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors focus-visible:ring-2 focus-visible:outline-none',
        isActive
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-border bg-background text-muted-foreground hover:text-foreground',
      )}
    >
      {label}
    </a>
  );
}

function PriceTag({
  amount,
  prefix,
  className,
}: {
  amount: number;
  prefix?: string;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'flex shrink-0 items-center gap-1 font-semibold',
        className,
      )}
    >
      {prefix && (
        <span className="text-muted-foreground text-xs font-normal">
          {prefix}
        </span>
      )}
      <CurrencyIcon className="h-4 w-4" />
      {amount.toFixed(2)}
    </span>
  );
}

function SingleServiceRow({
  template,
  name,
  description,
  price,
  isSelected,
  durationLabel,
  onSelect,
}: {
  template: TemplateInfo;
  name: string;
  description: string;
  price: number;
  isSelected: boolean;
  durationLabel: string;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      data-test="service-list-item"
      data-template-id={template.id}
      onClick={onSelect}
      className={cn(
        'flex w-full items-center justify-between gap-3 rounded-lg border p-3 text-start transition-all hover:shadow-sm',
        isSelected ? 'border-primary ring-primary ring-2' : 'border-border',
      )}
    >
      <div className="min-w-0 flex-1">
        <p className="font-medium break-words">{name}</p>
        <p className="text-muted-foreground text-xs">{durationLabel}</p>
        {description && (
          <p className="text-muted-foreground mt-1 line-clamp-2 text-xs">
            {description}
          </p>
        )}
      </div>

      <PriceTag amount={price} />
    </button>
  );
}

function GroupedServiceCard({
  name,
  description,
  options,
  isMember,
  isOpen,
  selectedTemplateId,
  formatDuration,
  localized,
  onToggle,
  onSelectOption,
}: {
  name: string;
  description: string;
  options: TemplateInfo[];
  isMember: boolean;
  isOpen: boolean;
  selectedTemplateId: string;
  formatDuration: (minutes: number) => string;
  localized: (primary: string, alt: string | null | undefined) => string;
  onToggle: () => void;
  onSelectOption: (option: TemplateInfo) => void;
}) {
  const { t } = useTranslation('bookings');
  const [showFullDescription, setShowFullDescription] = useState(false);

  const isSelected = options.some((option) => option.id === selectedTemplateId);
  const prices = options.map((option) => customerPrice(option, isMember));
  const fromPrice = Math.min(...prices);
  const minDuration = options[0]!.duration;
  const maxDuration = options[options.length - 1]!.duration;
  const durationRange =
    minDuration === maxDuration
      ? formatDuration(minDuration)
      : t('serviceOptions.durationRange', {
          min: formatDuration(minDuration),
          max: formatDuration(maxDuration),
        });

  const needsClamp = description.length > DESCRIPTION_CLAMP;
  const visibleDescription =
    needsClamp && !showFullDescription
      ? `${description.slice(0, DESCRIPTION_CLAMP).trimEnd()}…`
      : description;

  return (
    <div
      data-test="service-list-group"
      className={cn(
        'overflow-hidden rounded-lg border transition-all',
        isSelected ? 'border-primary ring-primary ring-2' : 'border-border',
      )}
    >
      <button
        type="button"
        aria-expanded={isOpen}
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-3 p-3 text-start hover:shadow-sm"
      >
        <div className="min-w-0 flex-1">
          <p className="font-medium break-words">{name}</p>
          <p className="text-muted-foreground text-xs">{durationRange}</p>
        </div>

        <PriceTag amount={fromPrice} prefix={t('serviceOptions.fromPrice')} />

        <ChevronDown
          aria-hidden
          className={cn(
            'text-muted-foreground h-4 w-4 shrink-0 transition-transform',
            isOpen && 'rotate-180',
          )}
        />
      </button>

      {isOpen && (
        <div className="bg-muted/40 space-y-2 border-t p-2">
          {description && (
            <p className="text-muted-foreground px-1 pt-1 text-xs leading-relaxed">
              {visibleDescription}
              {needsClamp && (
                <>
                  {' '}
                  <button
                    type="button"
                    className="text-primary font-medium underline-offset-2 hover:underline"
                    onClick={() => setShowFullDescription((v) => !v)}
                  >
                    {showFullDescription
                      ? t('serviceOptions.readLess')
                      : t('serviceOptions.readMore')}
                  </button>
                </>
              )}
            </p>
          )}

          <div className="text-muted-foreground flex items-baseline justify-between px-1 text-[11px] font-medium tracking-wide uppercase">
            <span>{t('serviceOptions.title')}</span>
            <span className="font-normal normal-case">
              {t('serviceOptions.required')}
            </span>
          </div>

          <div
            role="radiogroup"
            className="space-y-1.5"
            // WAI-ARIA radiogroup: one Tab stop (the checked option, else the
            // first), arrows move the selection.
            onKeyDown={(event) => {
              const step =
                event.key === 'ArrowDown' || event.key === 'ArrowRight'
                  ? 1
                  : event.key === 'ArrowUp' || event.key === 'ArrowLeft'
                    ? -1
                    : 0;
              if (step === 0) return;
              event.preventDefault();
              const currentIndex = Math.max(
                0,
                options.findIndex((option) => option.id === selectedTemplateId),
              );
              const next =
                options[
                  (currentIndex + step + options.length) % options.length
                ]!;
              onSelectOption(next);
              (
                event.currentTarget.querySelector(
                  `[data-template-id="${next.id}"]`,
                ) as HTMLButtonElement | null
              )?.focus();
            }}
          >
            {options.map((option, index) => {
              const optionSelected = option.id === selectedTemplateId;
              const isTabStop = isSelected ? optionSelected : index === 0;
              const label = localized(
                option.variant_label ?? option.name,
                option.variant_label_alt,
              );
              return (
                <button
                  key={option.id}
                  type="button"
                  role="radio"
                  aria-checked={optionSelected}
                  tabIndex={isTabStop ? 0 : -1}
                  data-test="service-list-option"
                  data-template-id={option.id}
                  onClick={() => onSelectOption(option)}
                  className={cn(
                    'bg-background flex w-full items-center gap-3 rounded-md border p-2.5 text-start transition-colors',
                    optionSelected
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-muted-foreground/40',
                  )}
                >
                  <span
                    aria-hidden
                    className={cn(
                      'grid h-4 w-4 shrink-0 place-items-center rounded-full border',
                      optionSelected
                        ? 'border-primary'
                        : 'border-muted-foreground/50',
                    )}
                  >
                    {optionSelected && (
                      <span className="bg-primary h-2 w-2 rounded-full" />
                    )}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium break-words">
                      {label}
                    </span>
                    <span className="text-muted-foreground block text-xs">
                      {formatDuration(option.duration)}
                    </span>
                  </span>
                  <PriceTag amount={customerPrice(option, isMember)} />
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
